import React, { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { Building, Save, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { COMPANY_TYPES } from '../../../../constants/companyTypes';
import InfoHint from '../../SharedComponents/InfoHint';
import SettingsSelect from '../SettingsSelect';
import { enrichCompanyDetails, lookupStatusMessage } from '../../../../services/api/companyLookupApi';

// Company details section — basic info (name/org.nr/VAT status), address and
// contact info. State and save live in the CompanySettings shell and arrive as
// props. Uses the global settings styles from the shell.
//
// "Hämta uppgifter" asks the backend to refresh registry facts (F-skatt, SNI,
// registration date, ...) from TIC and re-fetches — it updates only the
// read-only enrichment columns, never the user-editable fields.

const CompanyDetailsSection = ({
  companyDetails,
  setCompanyDetails,
  saveCompanyDetails,
  saveLegacyK2Correction,
  canCorrectLegacyAccountingRegime,
  companyId,
  onEnriched,
}) => {
  const [isEnriching, setIsEnriching] = useState(false);
  const [legacyK2Confirmed, setLegacyK2Confirmed] = useState(false);
  const handleEnrich = async () => {
    setIsEnriching(true);
    try {
      const result = await enrichCompanyDetails(companyId);
      if (result.enriched) {
        toast.success('Uppgifter hämtade från företagsregistret.');
        if (onEnriched) await onEnriched();
      } else {
        toast.error(
          lookupStatusMessage(result.status) ||
          'Kunde inte hämta uppgifter från företagsregistret just nu.'
        );
      }
    } finally {
      setIsEnriching(false);
    }
  };

  const registryFacts = [
    companyDetails.fTaxRegistered !== null && companyDetails.fTaxRegistered !== undefined
      ? { label: 'F-skatt', value: companyDetails.fTaxRegistered ? 'Registrerad' : 'Ej registrerad' }
      : null,
    companyDetails.sniCode
      ? { label: 'SNI', value: `${companyDetails.sniCode}${companyDetails.sniDescription ? ` — ${companyDetails.sniDescription}` : ''}` }
      : null,
    companyDetails.companyRegistrationDate
      ? { label: 'Registrerat', value: String(companyDetails.companyRegistrationDate).slice(0, 10) }
      : null,
    companyDetails.registeredOffice
      ? { label: 'Säte', value: companyDetails.registeredOffice }
      : null,
    companyDetails.employeeCount !== null && companyDetails.employeeCount !== undefined
      ? { label: 'Anställda', value: `~${companyDetails.employeeCount}` }
      : null,
  ].filter(Boolean);

  return (
  <div className="modern-card">
    <div className="modern-card-header">
      <div>
        <h2 className="section-title">
          <Building size={16} />
          Företagsinformation
        </h2>
        <p className="section-subtitle">
          Uppdatera dina företagsuppgifter som visas på fakturor och offerter
        </p>
      </div>
      <div className="action-buttons">
        <Button
          variant="outline-primary"
          className="d-flex align-items-center gap-2"
          onClick={handleEnrich}
          disabled={isEnriching}
          title="Hämta registeruppgifter (F-skatt, SNI, registreringsdatum) från företagsregistret"
        >
          {isEnriching ? <Spinner size="sm" className="me-2" /> : <RefreshCw className="me-2" size={16} />}
          Hämta uppgifter
        </Button>
        <Button
          variant="primary"
          className="d-flex align-items-center gap-2"
          onClick={saveCompanyDetails}
        >
          <Save className="me-2" size={16} />
          Spara företagsuppgifter
        </Button>
      </div>
    </div>

    <div className="modern-card-body">
      {registryFacts.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem 1.5rem',
            padding: '0.75rem 1rem',
            marginBottom: '1.5rem',
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '0.8125rem',
            color: '#374151'
          }}
          data-testid="registry-facts"
        >
          {registryFacts.map((fact) => (
            <span key={fact.label}>
              <span style={{ color: '#6b7280' }}>{fact.label}:</span>{' '}
              <span style={{ fontWeight: 600 }}>{fact.value}</span>
            </span>
          ))}
          {companyDetails.ticEnrichedAt && (
            <span style={{ color: '#9ca3af', marginLeft: 'auto' }}>
              Uppdaterat {String(companyDetails.ticEnrichedAt).slice(0, 10)}
            </span>
          )}
        </div>
      )}
      <div className="company-form-grid">
        {/* Basic Information Section */}
        <div className="form-section-wrapper" id="grunduppgifter">
          <div className="section-header-external">
            <div className="section-icon">
              <Building size={20} />
            </div>
            <div>
              <h3 className="section-heading-external">Grunduppgifter</h3>
              <p className="section-description">Företagets grundläggande information</p>
            </div>
          </div>

          <div className="form-section">
            <div className="form-fields">
              <div className="form-field half-width">
                <label htmlFor="companyName" className="modern-label">
                  Företagsnamn *
                </label>
                <input
                  type="text"
                  className="modern-input"
                  id="companyName"
                  placeholder="Ange företagsnamn"
                  value={companyDetails.companyName}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, companyName: e.target.value })}
                />
              </div>

              <div className="form-field half-width">
                <label htmlFor="companyType" className="modern-label">
                  Företagsform *
                  {companyDetails.companyType && (
                    <InfoHint text="Företagsform är en styrande identitetsuppgift och kan inte ändras som en vanlig profiluppgift." />
                  )}
                </label>
                <SettingsSelect
                  id="companyType"
                  options={COMPANY_TYPES.map((type) => ({ value: type, label: type }))}
                  value={companyDetails.companyType}
                  isDisabled={Boolean(companyDetails.companyType)}
                  onChange={(value) => setCompanyDetails({ ...companyDetails, companyType: value })}
                  placeholder="Välj företagsform"
                />
              </div>

              <div className="form-field half-width">
                <label htmlFor="registrationNumber" className="modern-label">
                  Organisationsnummer *
                </label>
                <input
                  type="text"
                  className="modern-input"
                  id="registrationNumber"
                  placeholder="XXXXXX-XXXX"
                  value={companyDetails.registrationNumber}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, registrationNumber: e.target.value })}
                />
              </div>

              {COMPANY_TYPES.includes(companyDetails.companyType) && (
                <div className="form-field full-width">
                  <label htmlFor="accountingRegime" className="modern-label">
                    Redovisningsregelverk
                    <InfoHint text="Ett fastställt K1/K2/K3-val kan inte ändras här. Ett byte kräver en separat kontrollerad övergång med behörighetsbedömning, ikraftträdande räkenskapsår och övergångsåtgärder." />
                  </label>
                  <input
                    id="accountingRegime"
                    className="modern-input"
                    style={{ maxWidth: 420 }}
                    value={companyDetails.accountingRegime
                      ? companyDetails.accountingRegime.toUpperCase()
                      : companyDetails.companyType === 'Enskild firma'
                        ? 'K1 (kanoniskt standardvärde, inte uttryckligen valt)'
                        : ['Aktiebolag', 'Ekonomisk förening'].includes(companyDetails.companyType)
                          ? 'K2 (kanoniskt standardvärde, inte uttryckligen valt)'
                          : 'Inte uttryckligen klassificerat'}
                    disabled
                    readOnly
                  />
                  {companyDetails.companyType === 'Enskild firma'
                    && !companyDetails.accountingRegime && (
                    <div className="mt-3 p-3 rounded" style={{ background: '#fff7ed', border: '1px solid #fed7aa' }}>
                      <p className="mb-2" style={{ fontSize: '0.875rem' }}>
                        Den här äldre uppgiften saknar ett uttryckligt val och behandlas därför
                        som K1. Engångskorrigeringen nedan får bara användas om företaget redan
                        tillämpar K2 enligt BFNAR 2017:3. Den genomför inget regelverksbyte och
                        bedömer inte om företaget får använda K2.
                      </p>
                      {canCorrectLegacyAccountingRegime ? (
                        <>
                          <label className="d-flex align-items-start gap-2 mb-2" htmlFor="legacyK2Confirmed">
                            <input
                              id="legacyK2Confirmed"
                              type="checkbox"
                              checked={legacyK2Confirmed}
                              onChange={(event) => setLegacyK2Confirmed(event.target.checked)}
                            />
                            <span>Jag bekräftar att företaget redan tillämpar K2 och att detta inte är ett byte av regelverk.</span>
                          </label>
                          <Button
                            variant="outline-primary"
                            onClick={saveLegacyK2Correction}
                            disabled={!legacyK2Confirmed}
                          >
                            Registrera befintligt K2-regelverk
                          </Button>
                        </>
                      ) : (
                        <small className="text-muted">Endast företagets ägare kan registrera engångskorrigeringen.</small>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Momsnummer, momsstatus, undantagsgrund och momsprofil have
                  moved to Inställningar → Bokföring → Moms, next to
                  avdragsprofilen and registreringshistoriken they belong with
                  (CompanyVatFields). They save through the same
                  saveCompanyDetails as the fields here. */}
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div className="form-section-wrapper" id="adress">
          <div className="section-header-external">
            <div className="section-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <div>
              <h3 className="section-heading-external">Adressinformation</h3>
              <p className="section-description">Företagets postadress och kontaktuppgifter</p>
            </div>
          </div>

          <div className="form-section">
            <div className="form-fields">
              <div className="form-field full-width">
                <label htmlFor="address" className="modern-label">
                  Gatuadress *
                </label>
                <input
                  type="text"
                  className="modern-input"
                  id="address"
                  placeholder="Ange gatuadress"
                  value={companyDetails.address}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, address: e.target.value })}
                />
              </div>

              <div className="form-field half-width">
                <label htmlFor="postalCode" className="modern-label">
                  Postnummer *
                </label>
                <input
                  type="text"
                  className="modern-input"
                  id="postalCode"
                  placeholder="123 45"
                  value={companyDetails.postalCode}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, postalCode: e.target.value })}
                />
              </div>

              <div className="form-field half-width">
                <label htmlFor="city" className="modern-label">
                  Ort *
                </label>
                <input
                  type="text"
                  className="modern-input"
                  id="city"
                  placeholder="Ange ort"
                  value={companyDetails.city}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, city: e.target.value })}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="form-section-wrapper" id="kontakt">
          <div className="section-header-external">
            <div className="section-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </div>
            <div>
              <h3 className="section-heading-external">Kontaktuppgifter</h3>
              <p className="section-description">Telefon, e-post och kontaktpersoner</p>
            </div>
          </div>

          <div className="form-section">
            <div className="form-fields">
              <div className="form-field half-width">
                <label htmlFor="companyPhone" className="modern-label">
                  Telefonnummer
                </label>
                <input
                  type="tel"
                  className="modern-input"
                  id="companyPhone"
                  placeholder="08-123 456 78"
                  value={companyDetails.companyPhone}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, companyPhone: e.target.value })}
                />
              </div>

              <div className="form-field half-width">
                <label htmlFor="companyEmail" className="modern-label">
                  E-postadress
                </label>
                <input
                  type="email"
                  className="modern-input"
                  id="companyEmail"
                  placeholder="info@företag.se"
                  value={companyDetails.companyEmail}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, companyEmail: e.target.value })}
                />
              </div>

              <div className="form-field half-width">
                <label htmlFor="contactPerson" className="modern-label">
                  Kontaktperson
                </label>
                <input
                  type="text"
                  className="modern-input"
                  id="contactPerson"
                  placeholder="Ange kontaktperson"
                  value={companyDetails.contactPerson}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, contactPerson: e.target.value })}
                />
              </div>

              <div className="form-field half-width">
                <label htmlFor="contactEmail" className="modern-label">
                  Kontakt E-post
                </label>
                <input
                  type="email"
                  className="modern-input"
                  id="contactEmail"
                  placeholder="kontakt@företag.se"
                  value={companyDetails.contactEmail}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, contactEmail: e.target.value })}
                />
              </div>

              <div className="form-field half-width">
                <label htmlFor="contactPhone" className="modern-label">
                  Kontakt Telefon
                </label>
                <input
                  type="tel"
                  className="modern-input"
                  id="contactPhone"
                  placeholder="070-123 45 67"
                  value={companyDetails.contactPhone}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, contactPhone: e.target.value })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

export default CompanyDetailsSection;
